import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  Image,
  Select,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  Avatar,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  NumberInput,
  NumberInputField,
  useInterval,
  Link,
  Show,
  useTheme,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import { useContext, useState, useRef, useEffect, ReactNode, useCallback, useMemo } from 'react';
import Head from 'next/head';
import { FiPlusCircle } from 'react-icons/fi';
import { RiAuctionFill } from 'react-icons/ri';
import {
  AddListingBanner,
  AttributeValuesDisplay,
  Breadcrumb,
  PriceTag,
  DateSinceAdded,
  LastUpdated,
  RelistAuctionButton,
  LoginPrompt,
  ErrorMessageBox,
  SampleListingsBanner,
} from 'src/components/atoms';
import {
  ListingGallery,
  ISoldThis,
  ViewMoreListings,
  ContactSellerModal,
} from 'src/components/molecules';
import { LoginModal, ShareButton } from 'src/components/organisms';
import {
  Bid,
  Listing,
  MarketplaceInfo,
  MaxBid,
  MarketplaceRole,
  Parent,
  CategoryFunctional,
} from 'src/api/v1-api';
import { CONTAINER_MAX_WIDTH_1, CONTAINER_MAX_WIDTH_2 } from 'src/constants/ui';
import { useDeepEffect } from 'src/utils/hooks';
import { useRouter } from 'next/router';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import { MarketplaceContext } from 'src/contexts/marketplace';
import {
  getAttributeOptions,
  getValueObjectValue,
  isDisplayAttribute,
} from 'src/utils/attributeUtils';
import {
  formatPrice,
  formatToLocalDateTime,
  getDisplayStatus,
  getTimezoneAbbreviation,
  utcISOToLocalDate,
} from 'src/utils/common';
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next';
import { gaEvent } from 'src/utils/gtag';
import { NextSeo, ProductJsonLd } from 'next-seo';
import CategorySelectorModal from 'src/components/molecules/category-selector-modal';
import { reduce } from 'lodash';
import useRequest from 'src/utils/hooks/useRequest';
import { getListingUrl, getMarketplaceUrl } from 'src/utils/marketplaceUtils';
import { getSocialProfileIcon } from 'src/utils/socialProfileIcons';
import { PaymentAlert } from 'src/components/atoms/payment-alert';
import {
  determineAuctionInfo,
  determineShowListing,
  filterVariants,
  getAvailableVariants,
  getListingSlug,
  getSelectedVariant,
} from 'src/utils/listingUtils';
import { FilePath } from 'src/components/organisms/listing-form';
import { srcFromCDN } from 'src/utils/files';
import {
  categoryTypeHasPrice,
  categoryTypeLeadOrDirectory,
  fetchCategoryPermissionsRoles,
} from 'src/utils/categoryUtils';
import InfoTooltip from 'src/components/atoms/info-tooltip';
import Countdown from 'src/components/atoms/countdown';
import AddUserRoleModal from 'src/components/molecules/add-user-role-modal';
import { checkCategoryPermissions } from 'src/utils/categoryUtils';
import {
  ADMIN_ONLY_LISTING_STATUSES,
  AUTHOR_ONLY_LISTING_STATUSES,
  CRAIGSLIST,
  FACEBOOK,
  GOOGLE,
  EBAY,
  OTHER,
  PREFETCHED_LISTING_STATUSES,
  YELP,
} from 'src/constants/marketplace';
import * as Sentry from '@sentry/nextjs';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import { ListingPageSkeleton } from 'src/components/skeletons/listing-page-skeleton';
import SelectListingStatus from 'src/components/atoms/select-listing-status';
import posthog from 'posthog-js';
import ListingAnalytics from 'src/components/molecules/listing-analytics';
import ListingSelfReportedSoldPriceModal from 'src/components/molecules/listing-self-reported-sold-price-modal';
import CollectEmailsModule from 'src/components/atoms/collect-emails-module';
import { isSampleListing } from 'src/utils/marketplaceUtils';

const ViewListing = ({
  listingData,
  marketplace,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const router = useRouter();
  const showToast = useSingleToast();
  const {
    useClient,
    auto_share_listing,
    subStatus,
    forceDraft,
    followUpToken,
    followUpResponse,
    leadId,
  } = router.query;
  const {
    getToken,
    user,
    hasAdmin,
    hasStaff,
    isAuthenticated,
    hasRoles,
    hasRole,
    userLoaded,
    getLomaSessionId,
  } = useAuth();
  const {
    categories,
    fetchCategories,
    connectedSocialProfiles,
    setConnectedSocialProfiles,
    connectedSocialProfilesFetched,
    setConnectedSocialProfilesFetched,
    urlBase,
  } = useContext(MarketplaceContext);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenCategorySelectorModal,
    onOpen: onOpenCategorySelectorModal,
    onClose: onCloseCategorySelectorModal,
  } = useDisclosure();
  const {
    isOpen: isOpenLoginModal,
    onOpen: onOpenLoginModal,
    onClose: onCloseLoginModal,
  } = useDisclosure();

  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const listingId = listingData.id;
  const toast = useToast();
  const { api } = useRequest();
  const isAdmin = hasAdmin(marketplace?.id);
  const isStaffUser = hasStaff();
  const theme = useTheme();

  // only use the pre-fetched data if is active and fully present
  const [listing, setListing] = useState<Listing | null>(
    !PREFETCHED_LISTING_STATUSES.includes(listingData.status) ? null : listingData,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isToastActive, setIsToastActive] = useState(false);
  const [wasFallback, setWasFallback] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [showSharingModal, setShowSharingModal] = useState<boolean>(false);
  const [submitApprovalLoading, setSubmitApprovalLoading] = useState(false);
  const [carouselMediaFiles, setCarouselMediaFiles] = useState<FilePath[]>([]);
  const [mainMediaFiles, setMainMediaFiles] = useState<FilePath[]>([]);
  const hasFetchedRef = useRef(false);
  const [bids, setBids] = useState<Bid[]>([]);
  const [maxBid, setMaxBid] = useState<MaxBid | null>(null);
  const [topBidder, setTopBidder] = useState(false);
  const [openRegisterBidModal, setOpenRegisterBidModal] = useState(false);
  const [isBidder, setIsBidder] = useState(false);
  const [rolesToAdd, setRolesToAdd] = useState<MarketplaceRole[]>([]);
  const [hasSelectedRole, setHasSelectedRole] = useState<boolean | null>(null);
  const [displayAttributeCount, setDisplayAttributeCount] = useState<number>(0);
  const auctionInfo = listing?.auction_info
    ? determineAuctionInfo(listing?.auction_info)
    : undefined;
  const isAuction = !!auctionInfo?.id;
  const isAuctionActive = isAuction && auctionInfo?.status === 'admin_approved';
  // dates are all in UTC which is fine for comparison. Start/end dates are localized for display.
  const now = new Date();
  const auctionStart =
    isAuction && auctionInfo?.start ? utcISOToLocalDate(auctionInfo.start) : null;
  const auctionEnd = isAuction && auctionInfo?.end ? utcISOToLocalDate(auctionInfo.end) : null;
  const auctionEndTime = auctionEnd?.getTime();
  const timezoneAbbr = getTimezoneAbbreviation();
  const missingAuctionDates = !auctionInfo?.start || !auctionInfo?.end;
  // TODO: use sockets to handle precise auction start/end windows
  const bidIncrement = auctionInfo?.bid_increment ?? 5;
  const [minNewBid, setMinNewBid] = useState<number | null>(null);
  const [newBid, setNewBid] = useState<string>(bidIncrement.toString());
  const isNoAuctionOrEnded =
    !auctionInfo?.id ||
    (auctionInfo?.status === 'admin_approved' && auctionEnd && now > auctionEnd);
  const isAuctionLive =
    auctionInfo?.status === 'admin_approved' &&
    auctionStart &&
    auctionEnd &&
    auctionStart < now &&
    now < auctionEnd;
  const [defaultOverrides, setDefaultOverrides] = useState<Record<string, string>>({});

  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [permissionChecked, setPermissionChecked] = useState<boolean>(false);
  const [permissionsErrorMessage, setPermissionsErrorMessage] = useState<string | null>('');
  const [showBadBrowser, setShowBadBrowser] = useState<boolean>(false);
  const [startTimeout, setStartTimeout] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false);

  useEffect(() => {
    const slug = listingData.slug || getListingSlug(listingData.title, listingData.id);
    const path = `/listing/${slug}`;
    if (router.asPath !== path) {
      const currentUrl = new URL(window.location.href);
      currentUrl.pathname = path;
      window.history.replaceState(
        { ...window.history.state, as: currentUrl.toString(), url: currentUrl.toString() },
        '',
        currentUrl.toString(),
      );
    }
  }, [listingData]);

  useEffect(() => {
    const checkPermissions = async () => {
      const { hasPermission, errorMessage } = await checkCategoryPermissions(
        listing?.category?.id,
        marketplace?.id,
        user,
        hasRoles,
      );
      setHasPermission(hasPermission);
      setPermissionsErrorMessage(errorMessage);
      setPermissionChecked(true);
    };

    if (userLoaded && listing) {
      checkPermissions();
    }
  }, [listing, marketplace, user, userLoaded]);

  useEffect(() => {
    const overdefs = {} as Record<string, string>;
    for (const textover of marketplace.text_overrides ?? []) {
      overdefs[textover.name] = textover.default_value;
    }
    setDefaultOverrides(overdefs);
  }, [marketplace.text_overrides]);

  // if sold, will be the max bid amount unless reserve hasn't been reached by bidding - in that case, the top bid is bumped up to the reserve price
  const soldPrice =
    isAuction &&
    maxBid &&
    (auctionInfo?.reserve_price &&
    parseFloat(auctionInfo.reserve_price) > parseFloat(maxBid.max_bid_amount)
      ? auctionInfo.reserve_price
      : maxBid.max_bid_amount);

  const isAuthor =
    listing?.marketplace_account?.user?.id && user?.id
      ? listing.marketplace_account.user.id === user.id
      : false;
  const isAuctionEditableStatus =
    !!auctionInfo?.status &&
    ['draft', 'pending_admin_approval', 'admin_rejected'].includes(auctionInfo.status);
  const isAuthorEditable = isAuthor && (!isAuction || isAuctionEditableStatus);
  const showListing = listing
    ? isStaffUser ||
      isAdmin ||
      isAuthor ||
      determineShowListing(
        listing.status,
        !!marketplace?.functional?.show_inactive_listings_info,
        !!listing.category?.functional?.show_sold_items_in_listings,
      )
    : false;

  const shouldShowDescription = listing?.source === 'loma' || listing?.source === 'ebay';

  const shareListing = async (network: string) => {
    if (!marketplace || !listingId || urlBase === '') {
      return null;
    }

    try {
      setIsSharing(true);
      const listingUrl = getListingUrl(urlBase, Number(listingId));
      const payload = {
        caption: '',
        location: '',
        networks: [network],
        loma_url: listingUrl,
        create_promo_media: true,
        auto_share: true,
      };
      await api.shareautomation.listings.sharePromoMedia(
        marketplace.id,
        Number(listingId),
        payload,
      );

      toast({
        title: 'Success',
        description: `Listing has been shared successfully to ${network}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = error.message.includes('Listing has been already shared')
        ? `You have already used a Loma automated template to share this listing to ${network}.`
        : 'Something went wrong. Please try again.';

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSharing(false);
    }
  };

  const getConnectedSocialProfiles = async () => {
    if (!marketplace) return;

    try {
      setIsLoading(true);
      const response = await api.shareautomation.connectedSocialProfiles.list(marketplace.id);
      setConnectedSocialProfiles(response.connected_accounts);
      setConnectedSocialProfilesFetched(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((isAdmin || isStaffUser) && !connectedSocialProfilesFetched) {
      getConnectedSocialProfiles();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, marketplace, connectedSocialProfilesFetched]);

  useEffect(() => {
    const fetchCheckoutSession = async (marketplaceId: number, checkoutSessionId: string) => {
      const checkoutSession = await api.checkoutSession.get(marketplaceId, checkoutSessionId);
      if (checkoutSession.status === 'complete') {
        toast({
          title: 'Success',
          description:
            'Your payment was successfully processed! It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (router.query.success === 'true') {
      toast({
        title: 'Success',
        description: router.query.message as string,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    }

    if (router.isReady && router.query.checkout_session_id && marketplace) {
      const checkoutSessionId = router.query.checkout_session_id as string;
      fetchCheckoutSession(marketplace.id, checkoutSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, toast]);

  useEffect(() => {
    if (
      isAdmin &&
      router.isReady &&
      auto_share_listing === 'true' &&
      connectedSocialProfiles.length != 0 &&
      urlBase !== ''
    ) {
      if (connectedSocialProfiles.length === 1) {
        shareListing(connectedSocialProfiles[0].platform);
      } else {
        setShowSharingModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, isAdmin, auto_share_listing, connectedSocialProfiles, urlBase]);

  const leadFollowUp = async () => {
    if (!leadId || !followUpToken || !followUpResponse || !marketplace) return;
    try {
      const payload = {
        follow_up_token: followUpToken,
        response: followUpResponse,
      };

      await api.marketplace.leads.followUp(marketplace.id, parseInt(leadId as string), payload);
      if (toast.isActive('follow-up-success')) return;
      toast({
        id: 'follow-up-success',
        title: 'Success',
        description: 'Thank you for following up on this listing. Your response has been recorded.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      showToast({
        title: 'There was an error following up on this listing.',
        description: 'Please try again. Contact support if the issue persists.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace.id,
            leadId: leadId,
            followUpToken: followUpToken,
            followUpResponse: followUpResponse,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (router.isReady && followUpToken && followUpResponse && leadId && marketplace) {
      leadFollowUp();
    }
  }, [router.isReady, followUpToken, followUpResponse, leadId, marketplace]);

  useEffect(() => {
    if (router.isReady && forceDraft === 'true') {
      toast({
        title: 'Error',
        description:
          'Your listing was set to Draft mode because you have exceeded the maximum listings allowed for this category.  Deactivate or mark an active listing as sold in order to post more.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }
  }, [router.isReady, forceDraft]);

  useEffect(() => {
    let mounted = true;

    const checkIsBidder = async () => {
      if (!isAuctionLive || !listing?.category?.id || !userLoaded) return;

      try {
        const categoryRoles = await fetchCategoryPermissionsRoles(listing.category.id.toString());

        // Check if still mounted before any state updates
        if (!mounted) return;

        const canBidRolePermissions = categoryRoles.filter((rolePerm) => rolePerm.can_take_action);
        const canBidRoles = canBidRolePermissions.map((rolePerm) => rolePerm.role);
        const canBidRoleNames = canBidRoles
          .filter((role) => role.status == 'active')
          .map((role) => role.name);

        if ((user?.id && canBidRoles.length === 0) || hasRoles(marketplace.id, canBidRoleNames)) {
          if (mounted) setIsBidder(true);
        } else {
          if (marketplace && canBidRoleNames.length > 0 && mounted) {
            setHasSelectedRole(hasRole(marketplace.id, canBidRoleNames[0]));
          }
          if (mounted) setRolesToAdd(canBidRoles);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    checkIsBidder();

    return () => {
      mounted = false;
    };
  }, [userLoaded, isAuctionLive, listing?.category_id]);

  useEffect(() => {
    const handleSubStatus = async () => {
      const marketplace_id = listing?.category?.marketplace_id;
      if (!subStatus || !marketplace_id || !followUpToken || !listing) return;

      // user can only change sub status if they are an admin, the author of the listing, or a staff user
      // if (!isAdmin && !isAuthor && !isStaffUser) return;
      // TODO: Add token to url params to prevent unauthorized access, don't rely on user auth

      if (
        (subStatus === 'self_reported_loma_attributed' ||
          subStatus === 'self_reported_non_loma_attributed') &&
        listing?.status !== 'sold'
      ) {
        try {
          setIsLoading(true);
          const updatedListing = await api.marketplace.listings.setStatusAndSubsStatus(
            listing?.id,
            marketplace_id,
            'sold',
            subStatus,
            followUpToken as string,
          );
          setListing(updatedListing);
          showToast({
            title: 'Success',
            description: 'Listing marked as Sold!',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } catch (error) {
          showToast({
            title: 'Error',
            description: 'Error marking listing as Sold. Please try again later.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          console.error('error', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleSubStatus();
  }, [subStatus, listing, setIsLoading, setListing, toast]);

  const processMaxBid = (newMaxBid: MaxBid, isNewBid: boolean) => {
    if (newMaxBid && newMaxBid.id) {
      setMaxBid(newMaxBid);
    }
    const tempMinBid =
      parseFloat(newMaxBid?.bid_amount || newMaxBid?.max_bid_amount || '0.0') + bidIncrement;
    setMinNewBid(tempMinBid);

    if (isNewBid) {
      // don't prefill the newBid value to prevent the user from double clicking after bidding once
      setNewBid('');
    } else if (parseFloat(newBid) < tempMinBid) {
      setNewBid(tempMinBid.toString());
    }

    if (user?.id && newMaxBid.user_id == user.id) {
      setTopBidder(true);

      if (isNewBid) {
        toast({
          status: 'success',
          duration: 5000,
          isClosable: true,
          title: `Congratulations, you are the top bidder!`,
        });
      }
    } else if (isNewBid) {
      toast({
        status: 'success',
        duration: 5000,
        isClosable: true,
        title: `You have been outbid!`,
      });
    }
  };

  const fetchMaxBid = async (auctionId: number) => {
    setIsLoading(true);
    try {
      if (!user) {
        const response = await api.marketplace.auctions.anon_max_bid.get(auctionId);
        processMaxBid(response, false);
      } else {
        const response = await api.marketplace.auctions.max_bid.get(auctionId);
        processMaxBid(response, false);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load bids for this auction.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuctionBids = async (auctionId: number) => {
    if (!user) return;
    const bids = await api.marketplace.auctions.bids.list(auctionId);
    setBids(bids);
  };

  const fetchBidData = (auctionId: number) => {
    fetchMaxBid(auctionId);
    if (isStaffUser) {
      fetchAuctionBids(auctionId);
    }
  };

  useEffect(() => {
    if (!isAuction || !auctionInfo?.id || isAuctionEditableStatus) return;
    fetchBidData(auctionInfo.id);
    // user ensures that we fetch the max bid in relation to the logged-in user
  }, [isAuction, auctionInfo?.id, user]);

  useInterval(
    () => {
      if (!isAuctionLive || !auctionInfo?.id) return;
      fetchBidData(auctionInfo.id);
    },
    // update every minute
    60000,
  );

  const approveCandidateListing = async () => {
    setSubmitApprovalLoading(true);
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/${marketplace?.id}/approve/`;
    const token = await getToken();

    try {
      const response = await apiRequest('POST', url, token);
      toast({
        title: 'Success',
        description: 'Listing approved',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setListing(response);
    } catch (error) {
      displayErrorToast('Error', 'There was a problem approving this listing');
    } finally {
      setSubmitApprovalLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && categories.length === 0) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace?.id, categories, isAdmin]);

  useEffect(() => {
    // Refresh the page if the page transitioned from fallback to not fallback to display the correct data
    if (router.isFallback) {
      setWasFallback(true);
    }

    if (wasFallback && !router.isFallback && !isRefreshing && !hasFetchedRef.current) {
      setWasFallback(false);
      setIsRefreshing(true);
      // Prevent infinite loop of refreshing on the development server since it doesn't support fallback
      if (process.env.NODE_ENV !== 'development') {
        window.location.reload();
      }
    }
  }, [router.isFallback]);

  const displayErrorToast = (title: string, description: string) => {
    if (isToastActive) return;
    setIsToastActive(true);
    toast({
      title,
      description,
      status: 'error',
      duration: 5000,
      isClosable: true,
      onCloseComplete: () => setIsToastActive(false),
    });
  };

  // 1. First define the fetchData callback
  const fetchData = useCallback(async () => {
    let isMounted = true;

    if (router.query.prompt_login === 'true' && !isAuthenticated) {
      setShowLoginPrompt(true);
      setIsLoading(false);
      return;
    }

    // Refresh the page after successful login, without the prompt_login parameter
    if (router.query.prompt_login === 'true' && isAuthenticated) {
      const url = new URL(window.location.href);
      url.searchParams.delete('prompt_login');
      window.location.href = url.toString();
      return;
    }

    if (hasFetchedRef.current) return;

    setIsLoading(true);
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/`;
    const token = await getToken();

    let fetchedListingData = null;
    try {
      fetchedListingData = await apiRequest('GET', url, token);
    } catch (error) {
      if (!isMounted) return;
      displayErrorToast('Error', 'Listing not found');
      router.push('/');
      return;
    }

    if (!isMounted) return;

    if (!fetchedListingData || !fetchedListingData.status) {
      displayErrorToast('Error', 'Listing not found');
      router.push('/');
      return;
    }
    if (
      ADMIN_ONLY_LISTING_STATUSES.includes(fetchedListingData.status) &&
      !(isAdmin || isStaffUser)
    ) {
      displayErrorToast('Error', 'Listing not found');
      router.push('/');
      return;
    } else if (
      AUTHOR_ONLY_LISTING_STATUSES.includes(fetchedListingData.status) &&
      !(
        isAdmin ||
        isStaffUser ||
        (user?.id && fetchedListingData?.marketplace_account?.user.id === user?.id)
      )
    ) {
      displayErrorToast('Error', `Listing not found`);
      router.push('/');
      return;
    } else {
      setListing(fetchedListingData);
    }
    setIsLoading(false);
    hasFetchedRef.current = true;

    return () => {
      isMounted = false;
    };
  }, [
    router,
    isAuthenticated,
    listingId,
    getToken,
    isAdmin,
    isStaffUser,
    user?.id,
    onOpenLoginModal,
  ]);

  // 2. Then use the callback in useEffect
  useEffect(() => {
    if (
      userLoaded &&
      !isLoading &&
      (useClient || (listingData && !PREFETCHED_LISTING_STATUSES.includes(listingData.status)))
    ) {
      fetchData();
    }
  }, [fetchData, listingData, useClient, isLoading, userLoaded]);

  // Set the listing data if it's not set yet, used for refreshing listing on dev server
  useEffect(() => {
    if (listing) {
      countDisplayAttributes(listing);
    }
  }, [listingData, listing]);

  useEffect(() => {
    if (!listing) return;

    let carouselMediaFilesList: FilePath[] = [...carouselMediaFiles];
    let mainMediaFilesList: FilePath[] = [...mainMediaFiles];

    listing.listing_attribute_values?.forEach((attributeValue) => {
      if (attributeValue.attribute.format === 'media') {
        if (attributeValue.attribute.carousel_display) {
          carouselMediaFilesList = [
            ...carouselMediaFilesList,
            ...(attributeValue.value_object.values as FilePath[]),
          ];
        }
        if (attributeValue.attribute.main_section_display) {
          mainMediaFilesList = [
            ...mainMediaFilesList,
            ...(attributeValue.value_object.values as FilePath[]),
          ];
        }
      }
    });

    setCarouselMediaFiles(carouselMediaFilesList);
    setMainMediaFiles(mainMediaFilesList);
  }, [listing]);

  const [lineCount, setLineCount] = useState(0);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<{
    [key: string]: string | number;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // WeakMap to store ResizeObserver instances for DOM nodes
  const resizeObserversRef = useRef(new WeakMap<HTMLElement, ResizeObserver>());

  // Memoize heavy computations
  const availableVariants = useMemo(
    () => getAvailableVariants(listing?.variants),
    [listing?.variants],
  );
  const attributeOptions = useMemo(
    () => getAttributeOptions(availableVariants),
    [availableVariants],
  );

  const filteredVariants = filterVariants(availableVariants, selectedAttributeValues);
  const selectedVariant = getSelectedVariant(
    listing?.variants,
    filteredVariants,
    selectedAttributeValues,
    attributeOptions,
  );

  const isSelected = !!selectedVariant?.offered_price;

  const isValidOption = ({
    attributeName,
    option,
  }: {
    attributeName: string;
    option: string | number;
  }): boolean => {
    if (!attributeName || !option) return false;

    const validVariants = availableVariants.filter((variant) => {
      const invalidAttributeValues = (variant.variant_attribute_values ?? []).filter((item) => {
        const selectedValue = selectedAttributeValues[item.attribute.name];
        return attributeName === item.attribute.name || selectedValue === undefined
          ? false
          : getValueObjectValue(item.value_object)[0].value !== selectedValue;
      });

      return invalidAttributeValues.length === 0;
    });
    const validAttributeOptions = getAttributeOptions(validVariants);

    return (validAttributeOptions[attributeName] ?? []).includes(option);
  };

  // Callback ref that gets called when the element is actually attached
  const handleDescriptionRef = useCallback(
    (node: HTMLParagraphElement | null) => {
      const resizeObservers = resizeObserversRef.current;

      // Cleanup previous observer if exists
      if (descriptionRef.current) {
        const existingObserver = resizeObservers.get(descriptionRef.current);
        if (existingObserver) {
          existingObserver.disconnect();
          resizeObservers.delete(descriptionRef.current);
        }
      }

      if (node) {
        try {
          // Calculate initial line count immediately when element is available
          const initialHeight = node.clientHeight;
          const initialLineCount = initialHeight / 24;
          setLineCount(initialLineCount);

          // Set up ResizeObserver for future changes with error handling
          const resizeObserver = new ResizeObserver((entries) => {
            try {
              // Use entries array instead of direct node access
              const entry = entries[0];
              if (entry && entry.contentRect) {
                const newHeight = entry.contentRect.height;
                const newLineCount = newHeight / 24;
                setLineCount(newLineCount);
              }
            } catch (error) {
              // Silent fail for WebView issues - don't break the page
              console.warn('ResizeObserver callback error:', error);
            }
          });

          resizeObserver.observe(node);

          // Store observer in WeakMap for cleanup
          resizeObservers.set(node, resizeObserver);
        } catch (error) {
          // Fallback for environments where ResizeObserver fails
          console.warn('ResizeObserver initialization failed:', error);
          setLineCount(4); // Set a reasonable default
        }
      }

      // Update the regular ref for other code that might use it
      // This is safe because we're in a callback ref function
      (descriptionRef as React.MutableRefObject<HTMLParagraphElement | null>).current = node;
    },
    [], // No dependencies needed - ResizeObserver handles content changes automatically
  );

  useDeepEffect(() => {
    if (Object.keys(attributeOptions).length > 0) {
      Object.keys(attributeOptions).forEach((item) => {
        if (attributeOptions[item].length === 1) {
          setSelectedAttributeValues((prevValue) => ({
            ...prevValue,
            [item]: attributeOptions[item][0],
          }));
        }
      });
    }
  }, [attributeOptions]);

  // Cleanup effect for ResizeObservers
  useEffect(() => {
    return () => {
      // Clean up ResizeObserver for current node if it exists
      if (descriptionRef.current && resizeObserversRef.current) {
        const existingObserver = resizeObserversRef.current.get(descriptionRef.current);
        if (existingObserver) {
          try {
            existingObserver.disconnect();
          } catch (error) {
            // Silent fail during cleanup
            console.warn('Error disconnecting ResizeObserver:', error);
          }
          resizeObserversRef.current.delete(descriptionRef.current);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (startTimeout) {
      // if window.open fails to open, show a help message
      // may happen for some embedded browsers
      const badBrowserTimeout = setTimeout(() => {
        setShowBadBrowser(true);
        try {
          Sentry.captureMessage('Facebook embedded browser failed to open internal link', {
            level: 'error',
            tags: {
              component: 'ViewListing',
              marketplaceId: marketplace?.id,
            },
            extra: {
              userId: user?.id,
              isAdmin: isAdmin,
              isStaffUser: isStaffUser,
              listingId: listingId,
              mobileOS: getMobileOS(),
              userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
            },
          });
        } catch (error) {
          console.warn('Sentry capture failed:', error);
        }
      }, 1500);

      setTimeoutId(badBrowserTimeout);
      setStartTimeout(false);

      return () => {
        clearTimeout(badBrowserTimeout);
        setShowBadBrowser(false);
      };
    }
  }, [startTimeout]);

  type NewlineTextProps = {
    text: string;
  };

  const NewlineText: React.FC<NewlineTextProps> = ({ text }) => {
    const newText: ReactNode[] = text.split('\n').map((str, index, array) => {
      // Use content + index for a stable key that handles duplicate lines
      const key = `${str}-${index}`;
      return index === array.length - 1 ? (
        <span key={key}>{str}</span>
      ) : (
        <span key={key}>
          {str}
          <br />
        </span>
      );
    });

    return <>{newText}</>;
  };

  const isFacebookApp = () => {
    // detect if viewing the page from Facebooks internal browser
    try {
      if (typeof window === 'undefined' || !window.navigator || !('userAgent' in window.navigator))
        return false;
      const ua = window.navigator.userAgent;
      if (!ua) return false;
      return ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1;
    } catch (error) {
      // Handle cases where navigator is not accessible
      console.warn('Navigator access failed:', error);
      return false;
    }
  };

  const getMobileOS = () => {
    try {
      const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

      // Windows Phone must come first because its UA also contains "Android"
      if (/windows phone/i.test(userAgent)) return 'Windows Phone';
      if (/android/i.test(userAgent)) return 'Android';
      if (/iPad|iPhone|iPod/g.test(userAgent)) return 'iOS';

      return 'unknown';
    } catch (error) {
      console.warn('Navigator.userAgent access failed:', error);
      return 'unknown';
    }
  };

  const defaultCategoryCTA = useMemo(() => {
    /* Get the custom CTA for the category, or any parent category */
    let defaultCTA;
    defaultCTA = listing?.category?.functional?.text_overrides?.default_category_cta || undefined;
    if (defaultCTA) return defaultCTA;

    // check for a parent category CTA
    let parent = listing?.category?.parent;
    let parentCategory;
    while (parent && Array.isArray(categories)) {
      parentCategory = categories.find((cat) => cat.id === parent?.id);
      defaultCTA = parentCategory?.functional?.text_overrides?.default_category_cta || undefined;
      parent = parent.parent;
      if (defaultCTA || !parent) break;
    }
    return defaultCTA;
  }, [categories, listing?.category?.functional?.text_overrides?.default_category_cta]);

  const categoryTypeCTA = useMemo(() => {
    let typeCTA;
    if (listing?.category?.type === 'directory') {
      typeCTA =
        listing?.category?.functional?.text_overrides?.directory_cta ||
        defaultOverrides.directory_cta;
    } else if (listing?.category?.type === 'leads') {
      typeCTA =
        listing?.category?.functional?.text_overrides?.lead_cta || defaultOverrides.lead_cta;
    }
    return typeCTA;
  }, [listing?.category, defaultOverrides]);

  const showImageGallery = listing?.listing_images.length !== 0;

  function generateCTAContent() {
    if (isAuction) {
      if (isAuctionLive && !!user && !isBidder) {
        return {
          content: <>Register to Bid</>,
          props: {
            colorScheme: 'primary',
            size: 'lg',
            width: '75%',
            onClick: () => {
              posthog.capture('listing_cta_click', {
                listing_id: listing?.id,
                category_id: listing?.category?.id,
                marketplace_id: marketplace.id,
                action_type: 'register_to_bid',
              });
              setOpenRegisterBidModal(true);
            },
            isLoading,
          },
          startIcon: null,
          endIcon: <Icon as={RiAuctionFill} ml="2" />,
        };
      } else if (!isAuctionLive) {
        return {
          content: <>{auctionEnd && auctionEnd < now ? 'Auction Ended' : 'Auction Pending'}</>,
          props: {
            size: 'lg',
            width: '75%',
            isDisabled: true,
          },
          startIcon: null,
          endIcon: <Icon as={RiAuctionFill} ml="2" />,
        };
      }
    } else if (listing?.external_url) {
      let external_url = listing?.external_url;

      let checkFacebookBrowser = false;
      const isFBApp = isFacebookApp();
      if (listing?.source === 'facebook' && external_url && isFBApp) {
        // extract id from url like https://www.facebook.com/marketplace/item/1234567890
        const facebookPageId = external_url.split('/').pop();
        const mobileOS = getMobileOS();
        if (mobileOS === 'iOS') {
          external_url = `fb://marketplace_product_details?id=${facebookPageId}`;
        } else if (mobileOS === 'Android') {
          external_url = `intent://marketplace_product_details/?id=${facebookPageId}#Intent;package=com.facebook.katana;scheme=fb;end`;
        }
        checkFacebookBrowser = true;
      }
      let CTAButtonText =
        defaultCategoryCTA ??
        categoryTypeCTA ??
        defaultOverrides.default_category_cta ??
        'Checkout';

      if (listing?.source === CRAIGSLIST && defaultOverrides.cta_craigslist) {
        CTAButtonText = defaultOverrides.cta_craigslist;
      } else if (listing?.source === FACEBOOK && defaultOverrides.cta_facebook) {
        CTAButtonText = defaultOverrides.cta_facebook;
      } else if (listing?.source === EBAY && defaultOverrides.cta_ebay) {
        CTAButtonText = defaultOverrides.cta_ebay;
      } else if (listing?.source === GOOGLE && defaultOverrides.cta_google) {
        CTAButtonText = defaultOverrides.cta_google;
      } else if (listing?.source === YELP && defaultOverrides.cta_yelp) {
        CTAButtonText = defaultOverrides.cta_yelp;
      } else if (listing?.source === OTHER && defaultOverrides.cta_other) {
        CTAButtonText = defaultOverrides.cta_other;
      }
      return {
        content: listing.external_listing_info?.cta_text || CTAButtonText,
        props: {
          colorScheme: 'primary',
          size: 'lg',
          width: 'full',
          onClick: () => {
            if (!isAdmin && !isStaffUser) {
              gaEvent({
                action: 'listing_cta_click',
                category: 'engagement',
                label: 'external',
                value: listingId,
              });
              posthog.capture('listing_cta_click', {
                listing_id: listing?.id,
                category_id: listing?.category?.id,
                marketplace_id: marketplace.id,
                action_type: 'external_link',
                source: listing?.source,
              });
            }
            try {
              const newWindow = isFBApp
                ? window.open(external_url, '_top')
                : window.open(external_url);
              if (newWindow) {
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
              } else {
                checkFacebookBrowser && setStartTimeout(true);
              }
            } catch (error) {
              // Handle WebView restrictions gracefully
              console.warn('Window.open failed:', error);
              if (checkFacebookBrowser) {
                setStartTimeout(true);
              }
            }
          },
          isDisabled: false,
        },
        startIcon: null,
        endIcon: null,
      };
    } else if (categoryTypeLeadOrDirectory(listing?.category?.type)) {
      const listings_cta_text = defaultCategoryCTA ?? categoryTypeCTA ?? 'Contact Seller';

      return {
        content: <>{listings_cta_text}</>,
        props: {
          colorScheme: 'primary',
          size: 'lg',
          width: 'full',
          isDisabled: listing?.status !== 'active',
          onClick: () => {
            if (!isAdmin && !isStaffUser) {
              gaEvent({
                action: 'listing_cta_click',
                category: 'engagement',
                label: 'leads',
                value: listingId,
              });
              posthog.capture('listing_cta_click', {
                listing_id: listing?.id,
                category_id: listing?.category?.id,
                marketplace_id: marketplace.id,
                action_type: 'contact_seller',
              });
            }
            onOpen();
          },
          isLoading,
        },
        startIcon: <Icon as={FiPlusCircle} mr="2" />,
        endIcon: null,
      };
    } else {
      return {
        content: <>{listing?.category?.functional?.cart ? 'Add to cart' : 'Checkout'}</>,
        props: {
          colorScheme: 'primary',
          size: 'lg',
          width: 'full',
          isDisabled: !isSelected || listing?.status !== 'active',
          onClick: handleAddToCart,
          isLoading,
        },
        startIcon: <Icon as={FiPlusCircle} mr="2" />,
        endIcon: null,
      };
    }
    return {
      content: null,
      props: {},
      startIcon: null,
      endIcon: null,
    };
  }

  const handleAddNewBid = async () => {
    if (!minNewBid || Number(newBid) < minNewBid) return;
    if (!auctionInfo?.id) return;

    setIsLoading(true);
    try {
      const response = await api.marketplace.auctions.bids.create(auctionInfo.id, {
        bid_amount: newBid,
      });
      processMaxBid(response, true);
    } catch (error) {
      toast({
        status: 'error',
        duration: 5000,
        isClosable: true,
        title: `There was an error placing your bid`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    setIsLoading(true);

    // Track add to cart click
    if (!isAdmin && !isStaffUser) {
      gaEvent({
        action: 'listing_cta_click',
        category: 'engagement',
        label: 'add_to_cart',
        value: listingId,
      });
      posthog.capture('listing_cta_click', {
        listing_id: listing?.id,
        category_id: listing?.category?.id,
        marketplace_id: marketplace.id,
        action_type: 'add_to_cart',
        variant_id: selectedVariant.id,
        price: selectedVariant.offered_price || selectedVariant.regular_price,
      });
    }

    const url = `${process.env.NEXT_PUBLIC_API_HOST}/cart/add/${marketplace?.id}/${selectedVariant.id}/`;
    const token = await getToken();

    try {
      await apiRequest(
        'POST',
        url,
        token,
        { quantity: 1, lomasessionid: getLomaSessionId() },
        false,
      );
    } catch (e: unknown) {
      if (e instanceof Error) {
        const notEnoughStock = e.message.includes('Not enough stock');
        setErrorMessage(`Unable to add item to cart${notEnoughStock ? ': Not enough stock' : ''}`);
      } else {
        setErrorMessage('Unable to add item to cart');
      }
      setIsLoading(false);
      return;
    }

    if (listing?.category?.functional?.cart) {
      router.push(`/cart`);
    } else {
      router.push(`/checkout`);
    }
  };

  const {
    content: ctaContent,
    props: ctaProps,
    startIcon: ctaStartIcon,
    endIcon: ctaEndIcon,
  } = generateCTAContent();

  const sellerHandle = listing?.external_listing_info?.lister_handle
    ? listing?.external_listing_info?.lister_handle
    : listing?.marketplace_account?.handle;

  const capitalize = (str: string): string => {
    if (typeof str !== 'string' || str.length < 1) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const fetchClientSecret = useCallback(() => {
    if (!listing?.category?.id || urlBase === '') return Promise.resolve('');
    const listingUrl = getListingUrl(urlBase, listing.id);
    const payload = {
      return_url: listingUrl,
      client_reference_id: `${listing.id}-listing_id`,
    };
    return api.marketplace.categories.createCheckoutSession(listing?.category?.id, payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.category?.id]);

  const containsThirdPartyDisclaimer =
    !!listing?.external_listing_info?.disclaimer_text || !!listing?.generic_disclaimer_text;
  const firstListingImage = listing?.listing_images?.find((x) => x.position === 0)?.image || '';
  const variantLowestPrice = reduce(listing?.variants || [], (acc, curr) =>
    parseFloat(curr.offered_price || curr.regular_price || '0') <
    parseFloat(acc.offered_price || acc.regular_price || '0')
      ? curr
      : acc,
  );

  const lowestDisplayPrice = variantLowestPrice?.offered_price ?? variantLowestPrice?.regular_price;

  // Only display first image for Craigslist/Facebook/Google/Yelp/Other listings
  if (
    listing?.listing_images &&
    listing.listing_images.length > 0 &&
    listing.source &&
    [CRAIGSLIST, FACEBOOK, GOOGLE, YELP, OTHER].includes(listing.source)
  ) {
    const sortedListingImages = listing.listing_images.sort((a, b) => a.position - b.position);
    listing.listing_images = sortedListingImages.slice(0, 1);
  }

  const updateCategoryListingHandler = async (categoryId: string) => {
    if (!listing?.id || !categoryId) return;

    setIsLoading(true);
    const token = await getToken();

    try {
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listing.id}/${marketplace?.id}/update_category/`;
      const payload = {
        category_id: categoryId,
      };
      const updatedListing: Listing = await apiRequest('PUT', url, token, payload);
      setListing(updatedListing);

      toast({
        title: 'Success',
        description: 'Re-categorizing listing may take a minute',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Error updating listing category',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  let breadcrumb_parent: {
    id: number;
    name: string;
    slug: string;
    parent?: Parent;
    functional?: CategoryFunctional;
  } = listing?.category
    ? {
        id: listing.category.id,
        name: listing.category.name,
        slug: listing.category.slug,
        parent: listing.category.parent,
        functional: listing.category.functional,
      }
    : { id: 0, name: 'Home', slug: '/' };

  if (
    listing?.category?.parent &&
    (listing?.category?.functional?.create_listing_category_select_format === 'pay_to_list_child' ||
      listing?.category?.parent?.functional?.create_listing_category_select_format ===
        'pay_to_list_parent')
  ) {
    breadcrumb_parent = {
      id: listing.category.parent.id,
      name: listing.category.parent.name,
      slug: listing.category.parent.slug ?? '',
      parent: listing.category.parent.parent,
      functional: listing.category.parent.functional,
    };
  }

  const marketplaceUrl = getMarketplaceUrl(marketplace);
  const listingUrl = getListingUrl(marketplaceUrl, listingData.id);
  const imageUrl = firstListingImage ? srcFromCDN(firstListingImage, 800) : '';

  const viewListingTitle = `${listingData.title} | ${marketplace.name}`;

  const countDisplayAttributes = (listing: Listing) => {
    const attribute_values = listing?.listing_attribute_values?.filter((attribute_value) =>
      isDisplayAttribute(attribute_value, listing, marketplace, hasAdmin, hasStaff, user),
    );
    setDisplayAttributeCount(attribute_values?.length ?? 0);
  };

  // Track page views in PostHog
  useEffect(() => {
    // Only track if we have the necessary data and are showing the listing
    if (listing && showListing && typeof window !== 'undefined') {
      posthog.capture('listing_view', {
        listing_id: listing.id,
        category_id: listing.category?.id,
        marketplace_id: marketplace.id,
      });
    }
  }, [listing, showListing, marketplace.id, isAuction]);

  return (
    <>
      {!listing && showLoginPrompt ? (
        <>
          <Container maxW={CONTAINER_MAX_WIDTH_1} mt={10}>
            <LoginPrompt message="to view this page." showWithModalOpen />
          </Container>
        </>
      ) : (
        <>
          {!isAuction && (
            <>
              <NextSeo
                title={`${listingData.title} | ${marketplace.name}`}
                description={listingData.description ? listingData.description.slice(0, 120) : ''}
                canonical={listingUrl}
                openGraph={{
                  type: 'product',
                  url: listingUrl,
                  site_name: marketplace.name,
                  siteName: marketplace.name,
                  title: listingData.title,
                  description: listingData.description ? listingData.description.slice(0, 120) : '',
                  images: [
                    {
                      url: imageUrl,
                      width: 800,
                      height: 600,
                      alt: listingData.title,
                    },
                  ],
                }}
                twitter={{
                  site: listingUrl,
                  cardType: 'summary_large_image',
                }}
              />
              <ProductJsonLd
                productName={listingData.title}
                description={listingData.description ?? ''}
                images={[firstListingImage]}
                offers={{
                  price: lowestDisplayPrice ?? '0.00',
                  priceCurrency: 'USD',
                }}
              />
            </>
          )}
          <Head>
            <title>{viewListingTitle}</title>
            <link rel="preload" href={imageUrl} as="image" />
            {!isAuction && (
              <>
                <meta property="product:price:amount" content={lowestDisplayPrice} />
                <meta property="product:price:currency" content="USD" />
              </>
            )}
            {listing?.source && listing.source !== 'loma' && (
              <meta name="robots" content="noimageindex" />
            )}
          </Head>
          {permissionChecked ? (
            hasPermission ? (
              <>
                <AddListingBanner page="listing" />
                {isSampleListing(marketplace ?? null, listing?.source ?? '') && (
                  <SampleListingsBanner marketplace={marketplace} />
                )}
                {marketplace?.functional?.collect_email_module?.position === 'top' && (
                  <CollectEmailsModule
                    page="listing"
                    collect_email_module={marketplace?.functional?.collect_email_module}
                    marketplace={marketplace}
                  />
                )}
                <Container
                  maxW={CONTAINER_MAX_WIDTH_1}
                  pb={containsThirdPartyDisclaimer ? 0 : { base: 6, md: 8, lg: 12 }}
                >
                  {router.isFallback && <ListingPageSkeleton />}
                  <Box py={{ base: 4, md: 5, lg: 6 }}>
                    <Breadcrumb parent={breadcrumb_parent} current={undefined} />
                  </Box>
                  {listing &&
                    ![
                      'active',
                      'sold',
                      'pending_payment',
                      'pending',
                      'candidate',
                      'broken_image_candidate',
                      'broken_image_active',
                    ].includes(listing.status) && (
                      <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                        <Alert status="info" borderRadius="lg">
                          <AlertIcon />
                          This listing is currently in {getDisplayStatus(listing?.status)} mode and
                          is not visible to the public.
                        </Alert>
                      </Flex>
                    )}
                  {listing && (listing.status === 'candidate' || listing.status === 'pending') && (
                    <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                      <Alert status="info" borderRadius="lg">
                        <AlertIcon />
                        This listing is pending Admin approval and won&apos;t show up in Category
                        pages.
                      </Alert>
                    </Flex>
                  )}

                  {listing &&
                    listing.status === 'pending_payment' &&
                    !!listing?.category &&
                    !router.isFallback &&
                    urlBase !== '' && (
                      <PaymentAlert
                        fetchClientSecret={fetchClientSecret}
                        message="Complete your payment to make this listing visible to the public."
                        btnMessage="Pay to List"
                        alertMessage="Note: It may take up to a minute for the payment to process, try refreshing if the
                changes are not reflected immediately."
                        gaValue={listing.id}
                        gaSkip={isAdmin || isStaffUser}
                      />
                    )}

                  {showListing && listing?.status === 'sold' && (
                    <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                      <Alert status="success" borderRadius="lg">
                        <AlertIcon />
                        This listing has been sold!
                      </Alert>
                    </Flex>
                  )}

                  {listing &&
                    ['broken_image_candidate', 'broken_image_active'].includes(listing.status) && (
                      <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                        <Alert status="warning" borderRadius="lg">
                          <AlertIcon />
                          There was an issue loading the image for this listing.
                        </Alert>
                      </Flex>
                    )}

                  {!showListing && (
                    <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                      <Alert status="warning" borderRadius="lg">
                        <AlertIcon />
                        Unfortunately, this listing is no longer available.
                      </Alert>
                    </Flex>
                  )}

                  <Stack
                    direction={{ base: 'column', lg: 'row' }}
                    spacing={{ base: 3, lg: 6, xl: 8 }}
                  >
                    {showImageGallery && (
                      <ListingGallery
                        rootProps={{ overflow: 'hidden', flex: '1', rounded: 'md' }}
                        aspectRatio={{ base: 1 }}
                        images={showListing ? listing!.listing_images : []}
                        listingSource={listing?.source}
                        mediaFiles={mainMediaFiles}
                        marketplace={marketplace}
                      />
                    )}
                    {ctaContent && (
                      <Show below="lg">
                        {(lineCount >= 6 || displayAttributeCount >= 5) && (
                          <Tooltip
                            label={`If tapping button doesn't automatically redirect you, try opening this page in your dedicated mobile browser.`}
                            isOpen={showBadBrowser}
                          >
                            <Box m={0}>
                              <Button {...ctaProps} variant="link" size="xs" width="auto">
                                {ctaContent}
                              </Button>
                            </Box>
                          </Tooltip>
                        )}
                      </Show>
                    )}
                    <Flex direction={'column'} gap={{ base: 6, lg: 8 }} flex="1">
                      <Stack spacing={{ base: 3, md: 4 }}>
                        <VStack spacing="3" align="flex-start">
                          <Heading fontSize={{ base: '2xl', md: '3xl' }} fontWeight="semibold">
                            {listing?.title}
                          </Heading>
                          {!showImageGallery && (
                            <>
                              {isSampleListing(marketplace ?? null, listing?.source ?? '') ? (
                                <Badge
                                  p={2}
                                  rounded={theme.border_radius.border_radius_1}
                                  colorScheme="primary"
                                >
                                  Sample Listing
                                </Badge>
                              ) : (
                                listing.source !== 'loma' && (
                                  <Badge
                                    p={2}
                                    rounded={theme.border_radius.border_radius_1}
                                    colorScheme="primary"
                                  >
                                    {listing.source === CRAIGSLIST && 'Spotted on Craigslist'}
                                    {listing.source === FACEBOOK && 'Spotted on Facebook'}
                                    {listing.source === GOOGLE && 'Spotted on Google'}
                                    {listing.source === YELP && 'Spotted on Yelp'}
                                    {listing.source === OTHER && 'Spotted on the Web'}
                                  </Badge>
                                )
                              )}
                            </>
                          )}
                          <Flex justifyContent="flex-start" alignItems="center" gap="2" wrap="wrap">
                            {(isStaffUser || isAdmin || isAuthorEditable) && (
                              <Flex alignItems="center" gap={2}>
                                <Button
                                  colorScheme="primary"
                                  variant="outline"
                                  size="xs"
                                  onClick={() => router.push(`/listing/edit/${listing?.id}`)}
                                >
                                  Edit
                                </Button>
                              </Flex>
                            )}
                            {(isAuthor || isStaffUser || isAdmin) &&
                              listing?.status === 'active' &&
                              isNoAuctionOrEnded && (
                                <ISoldThis
                                  listing={listing}
                                  marketplace={marketplace}
                                  afterUpdate={(l) => setListing(l as Listing)}
                                  mode="edit"
                                />
                              )}
                            {(isAuthor || isStaffUser || isAdmin) &&
                              auctionInfo?.id &&
                              auctionInfo?.status === 'expired' && (
                                <RelistAuctionButton auctionId={auctionInfo.id} />
                              )}
                            {(isAdmin || isStaffUser) && (
                              <Flex alignItems="center">
                                <SelectListingStatus listing={listing ?? listingData} />
                                <ShareButton listing={listing ?? listingData} />
                              </Flex>
                            )}
                            {(isAdmin || isStaffUser) &&
                              (listing?.status === 'candidate' ||
                                listing?.status === 'pending') && (
                                <Flex alignItems="center">
                                  <Button
                                    colorScheme="green"
                                    variant="outline"
                                    size="xs"
                                    isLoading={submitApprovalLoading}
                                    onClick={approveCandidateListing}
                                    isDisabled={!!auctionInfo?.id && missingAuctionDates}
                                  >
                                    Approve
                                  </Button>
                                </Flex>
                              )}
                            {isAdmin && (
                              <Button
                                colorScheme="green"
                                variant="outline"
                                size="xs"
                                onClick={onOpenCategorySelectorModal}
                              >
                                Change Category
                              </Button>
                            )}
                          </Flex>
                        </VStack>
                        <HStack>
                          {isAdmin ? (
                            <>
                              <Link
                                href={`mailto:${listing?.marketplace_account?.user?.email}`}
                                fontWeight="semibold"
                              >
                                <Image
                                  borderRadius="full"
                                  boxSize="8"
                                  src={
                                    listing?.marketplace_account?.image_url ||
                                    listing?.marketplace_account?.icon_url
                                      ? srcFromCDN(
                                          listing?.marketplace_account?.image_url ||
                                            listing?.marketplace_account?.icon_url,
                                          32,
                                        )
                                      : undefined
                                  }
                                  alt="Seller Avatar"
                                  fallback={<Avatar size="sm" />}
                                />
                              </Link>
                              <Flex flexDirection="column" rowGap="-10px" fontSize="sm">
                                <Link
                                  href={`mailto:${listing?.marketplace_account?.user?.email}`}
                                  fontWeight="semibold"
                                >
                                  {sellerHandle}
                                </Link>
                              </Flex>
                            </>
                          ) : (
                            <>
                              <Image
                                borderRadius="full"
                                boxSize="8"
                                src={
                                  listing?.marketplace_account?.image_url ||
                                  listing?.marketplace_account?.icon_url
                                    ? srcFromCDN(
                                        listing?.marketplace_account?.image_url ||
                                          listing?.marketplace_account?.icon_url,
                                        32,
                                      )
                                    : undefined
                                }
                                alt="Seller Avatar"
                                fallback={<Avatar size="sm" />}
                              />
                              <Flex flexDirection="column" rowGap="-10px" fontSize="sm">
                                <Text fontWeight="semibold">{sellerHandle}</Text>
                              </Flex>
                            </>
                          )}
                        </HStack>
                        <ListingAnalytics listing={listing} />
                      </Stack>
                      {isAuction && (
                        <Stack spacing={3}>
                          {listing?.status === 'active' && (
                            <Text fontWeight="semibold" fontSize="xl">
                              Current Top Bid:{' '}
                              {maxBid
                                ? formatPrice(maxBid.max_bid_amount, { alwaysCents: false })
                                : 'no bids yet'}
                              {maxBid && (
                                <Text
                                  pl={4}
                                  fontWeight="norma"
                                  fontSize="xs"
                                  color="gray.400"
                                  as="span"
                                >
                                  {maxBid.bid_count ?? 0} {maxBid.bid_count === 1 ? 'bid' : 'bids'}
                                </Text>
                              )}
                            </Text>
                          )}
                          {listing?.status === 'sold' && (
                            <Text fontWeight="semibold" fontSize="xl">
                              Sold Price: {formatPrice(soldPrice, { alwaysCents: false })}
                              {maxBid && (
                                <Text
                                  pl={4}
                                  fontWeight="norma"
                                  fontSize="xs"
                                  color="gray.400"
                                  as="span"
                                >
                                  {maxBid.bid_count ?? 0} {maxBid.bid_count === 1 ? 'bid' : 'bids'}
                                </Text>
                              )}
                            </Text>
                          )}
                          {listing?.status === 'expired' && (
                            <Text fontWeight="semibold" fontSize="xl">
                              <i>Auction expired</i>
                              {maxBid && (
                                <Text
                                  pl={4}
                                  fontWeight="norma"
                                  fontSize="xs"
                                  color="gray.400"
                                  as="span"
                                >
                                  {maxBid.bid_count ?? 0} {maxBid.bid_count === 1 ? 'bid' : 'bids'}
                                </Text>
                              )}
                            </Text>
                          )}

                          {auctionInfo?.show_reserve &&
                            auctionInfo.reserve_price &&
                            parseFloat(maxBid?.max_bid_amount || '0') <
                              parseFloat(auctionInfo.reserve_price) && (
                              <Text fontWeight="semibold" color="gray.500" pl={5}>
                                Reserve Price:{' '}
                                {formatPrice(auctionInfo.reserve_price, { alwaysCents: false })}
                              </Text>
                            )}
                          {listing?.status !== 'sold' &&
                            !auctionInfo?.show_reserve &&
                            auctionInfo?.reserve_price &&
                            parseFloat(maxBid?.max_bid_amount || '0') <
                              parseFloat(auctionInfo.reserve_price) && (
                              <Text color="gray.500" pl={5}>
                                reserve not met
                              </Text>
                            )}

                          {isAuctionActive && auctionStart && auctionEndTime && (
                            <Text fontWeight="semibold" pl="5">
                              {isAuctionLive ? (
                                <Countdown endTime={auctionEndTime} format="mixed" extra=" left" />
                              ) : (
                                <Countdown
                                  endTime={auctionStart.getTime()}
                                  format="mixed"
                                  extra=" until start of auction"
                                />
                              )}
                            </Text>
                          )}

                          <HStack textColor="black" alignItems="flex-start" pt={4}>
                            <Text>
                              <b>Auction Start</b>:{' '}
                              {auctionInfo?.start
                                ? formatToLocalDateTime(auctionInfo.start) + ' ' + timezoneAbbr
                                : 'No start date set'}
                            </Text>
                          </HStack>
                          <HStack textColor="black" alignItems="flex-start" pb={4}>
                            <Text>
                              <b>Auction End</b>:{' '}
                              {auctionInfo?.end
                                ? formatToLocalDateTime(auctionInfo.end) + ' ' + timezoneAbbr
                                : 'No end date set'}
                            </Text>
                          </HStack>

                          {isAuction && (isAdmin || isAuthor) && (
                            <Box bgColor="yellow.50" border={'1px solid #ddd'} p={4}>
                              <Text fontWeight="bold">Admin Notes</Text>
                              <Text mt={2}>
                                {auctionInfo?.notes_for_seller ?? <i>No notes from admin.</i>}
                              </Text>
                              {isAuthorEditable && (
                                <Text mt={2} fontSize="sm" color="gray.600">
                                  <i>Send a note to the admin from the &quot;Edit&quot; screen</i>
                                </Text>
                              )}
                            </Box>
                          )}

                          {isAuction && isAdmin && (
                            <Box bgColor="yellow.50" border={'1px solid #ddd'} p={4}>
                              <Text fontWeight="bold">Seller Notes</Text>
                              <Text mt={2}>
                                {auctionInfo?.notes_for_admin ?? <i>No notes from seller.</i>}
                              </Text>
                            </Box>
                          )}

                          {isAuctionLive && isBidder && !isAuthor && (
                            <>
                              {topBidder && (
                                <Box bgColor="green.50" border={'1px solid #ddd'} p={4}>
                                  <Text fontWeight="bold">You are the top bidder!</Text>
                                  <Text mt={2}>
                                    Your current maximum bid is{' '}
                                    {formatPrice(maxBid?.bid_amount ?? maxBid?.max_bid_amount, {
                                      alwaysCents: false,
                                    })}
                                    {maxBid &&
                                      maxBid?.bid_amount &&
                                      maxBid.bid_amount > maxBid.max_bid_amount && (
                                        <span>&nbsp;and has not been reached</span>
                                      )}
                                    .
                                  </Text>
                                </Box>
                              )}
                              <FormControl pt="4" id="newBid">
                                <FormLabel>
                                  {topBidder ? 'Increase Your Maximum Bid' : 'Set Your Maximum Bid'}{' '}
                                  &nbsp;
                                  <InfoTooltip
                                    description="Bid on the auction item by setting your maximum bid. If you are the top bidder
                            at the close of auction, the price paid will be one bid increment above the second highest bid (not your full maximum bid).
                            If you are already the top bidder, you may increase your maximum bid amount."
                                  />
                                </FormLabel>
                                <InputGroup>
                                  <InputLeftAddon>$</InputLeftAddon>
                                  {minNewBid && (
                                    <NumberInput
                                      id="newBid"
                                      name="newBid"
                                      value={newBid}
                                      min={minNewBid}
                                      precision={2}
                                      onChange={(v) => setNewBid(v)}
                                    >
                                      <NumberInputField borderLeftRadius={0} />
                                    </NumberInput>
                                  )}
                                  <Text fontSize="xs" color="gray.500" mt="2" ml="2">
                                    Minimum bid increment: ${bidIncrement}
                                  </Text>
                                </InputGroup>
                              </FormControl>
                              <Button
                                colorScheme="primary"
                                size="lg"
                                width="full"
                                onClick={handleAddNewBid}
                                isLoading={isLoading}
                                isDisabled={!minNewBid || Number(newBid) < Number(minNewBid)}
                              >
                                Add Bid
                                <Icon as={RiAuctionFill} ml="2" />
                              </Button>
                            </>
                          )}
                          {isStaffUser &&
                            bids.map((bid) => {
                              return (
                                <Text key={bid.id} fontSize="xs" color="gray.500">
                                  {bid.id} by {bid.marketplace_account.user?.id}: ${bid.bid_amount}{' '}
                                  - {bid.created_at}
                                </Text>
                              );
                            })}
                        </Stack>
                      )}
                      <Stack spacing={3}>
                        {availableVariants.length > 0 &&
                          categoryTypeHasPrice(listing?.category?.type) && (
                            <>
                              {isSelected ? (
                                <PriceTag
                                  currency={listing?.currency}
                                  regularPrice={selectedVariant?.regular_price}
                                  offeredPrice={selectedVariant?.offered_price}
                                  rootProps={{ alignItems: 'flex-end' }}
                                  regularPriceProps={{
                                    fontSize: 'xl',
                                    lineHeight: 1.1,
                                    fontFamily: theme.fonts.font_2,
                                  }}
                                  offeredPriceProps={{
                                    fontSize: '2xl',
                                    lineHeight: 1,
                                    fontFamily: theme.fonts.font_2,
                                  }}
                                />
                              ) : (
                                <Text fontSize="xs" fontStyle="italic">
                                  Make selection to see price
                                </Text>
                              )}
                            </>
                          )}
                        {!isAuction && <DateSinceAdded listing={listingData} />}
                        {listingData?.category?.functional?.show_last_updated && (
                          <LastUpdated listing={listingData} />
                        )}
                        {ctaContent && (
                          <Show above="lg">
                            {lineCount >= 17 && (
                              <Tooltip
                                label={`If tapping button doesn't automatically redirect you, try opening this page in your dedicated mobile browser.`}
                                isOpen={showBadBrowser}
                              >
                                <Box m={0}>
                                  <Button {...ctaProps} variant="link" size="xs" width="auto">
                                    {ctaContent}
                                  </Button>
                                </Box>
                              </Tooltip>
                            )}
                          </Show>
                        )}
                        {lineCount < 4 &&
                          (shouldShowDescription ? (
                            <Text
                              ref={handleDescriptionRef}
                              wordBreak="break-word"
                              overflowWrap="break-word"
                            >
                              <NewlineText
                                text={listing?.description ? listing.description.trim() : ''}
                              />
                            </Text>
                          ) : (
                            <Text ref={handleDescriptionRef}>
                              To view the full description, please visit the original listing
                            </Text>
                          ))}
                        {showListing && (
                          <SimpleGrid
                            columns={
                              !showImageGallery && listing?.listing_attribute_values.length > 2
                                ? { base: 1, lg: 2 }
                                : { base: 1 }
                            }
                            spacing={2}
                          >
                            <AttributeValuesDisplay
                              attributeValues={listing?.listing_attribute_values || []}
                              listing={listing}
                              marketplace={marketplace}
                            />
                          </SimpleGrid>
                        )}
                      </Stack>
                      {Object.keys(attributeOptions).length > 0 && showListing && (
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 2, md: 4 }}>
                          {Object.keys(attributeOptions)
                            .filter((k) => attributeOptions[k].length > 0)
                            .map((k) => (
                              <Stack flex="1" key={k}>
                                <Text>{k}</Text>
                                <Select
                                  width="full"
                                  aria-label={k.toUpperCase()}
                                  focusBorderColor="primary.200"
                                  rounded="md"
                                  placeholder={`Select ${k}`}
                                  height="42px"
                                  value={selectedAttributeValues[k]}
                                  onChange={(e) =>
                                    setSelectedAttributeValues((prevValue) => {
                                      const newValue = { ...prevValue };
                                      if (e.target.value) {
                                        newValue[k] = e.target.value;
                                      } else {
                                        delete newValue[k];
                                      }
                                      return newValue;
                                    })
                                  }
                                >
                                  {attributeOptions[k].map((option) => (
                                    <option
                                      key={option}
                                      value={option}
                                      disabled={!isValidOption({ attributeName: k, option })}
                                    >
                                      {option}
                                    </option>
                                  ))}
                                </Select>
                              </Stack>
                            ))}
                        </SimpleGrid>
                      )}
                      {lineCount >= 4 &&
                        (shouldShowDescription ? (
                          <Text
                            ref={handleDescriptionRef}
                            wordBreak="break-word"
                            overflowWrap="break-word"
                          >
                            <NewlineText
                              text={listing?.description ? listing.description.trim() : ''}
                            />
                          </Text>
                        ) : (
                          <Text ref={handleDescriptionRef}>
                            To view the full description, please visit the original listing
                          </Text>
                        ))}
                      {errorMessage && (
                        <Text color="red.500" fontSize="xs" fontWeight="semibold">
                          {errorMessage}
                        </Text>
                      )}
                      {ctaContent && (
                        <Stack spacing={{ base: 6, lg: 8 }} flex="1" direction="column">
                          <HStack marginTop="auto" width="full">
                            <Tooltip
                              label={`If tapping button doesn't automatically redirect you, try opening this page in your dedicated mobile browser.`}
                              isOpen={showBadBrowser}
                            >
                              <Button {...ctaProps} data-test="cta-btn">
                                {ctaStartIcon}
                                {ctaContent}
                                {ctaEndIcon}
                              </Button>
                            </Tooltip>
                          </HStack>
                        </Stack>
                      )}
                      {isAuctionLive && !user && (
                        <Box m={0}>
                          <Button size="md" colorScheme="primary" onClick={onOpenLoginModal}>
                            {listing?.category?.functional?.text_overrides?.auction_login_btn ||
                              'Login to Bid'}
                          </Button>
                          <LoginModal isOpen={isOpenLoginModal} onClose={onCloseLoginModal} />
                        </Box>
                      )}
                    </Flex>
                  </Stack>
                </Container>

                {containsThirdPartyDisclaimer && (
                  <Container
                    maxW={CONTAINER_MAX_WIDTH_1}
                    pb={{ base: 6, md: 8, lg: 12 }}
                    display={'flex'}
                  >
                    {showImageGallery && <Box flexBasis={{ base: 0, lg: '50%' }}></Box>}
                    <Box
                      flexBasis={{ base: '100%', lg: showImageGallery ? '50%' : '100%' }}
                      px={{ base: 0, lg: 4 }}
                      pt={2}
                    >
                      {!!listing?.external_listing_info?.disclaimer_text && (
                        <Text fontStyle="italic" fontSize={'10px'} color="gray.700">
                          {listing?.external_listing_info?.disclaimer_text}
                        </Text>
                      )}
                      {!!listing?.generic_disclaimer_text && (
                        <Text fontStyle="italic" fontSize={'10px'} color="gray.700">
                          {listing?.generic_disclaimer_text}
                        </Text>
                      )}
                    </Box>
                  </Container>
                )}
                {listing?.category &&
                  showListing &&
                  listing.category.functional?.show_more_listings_on_view_listing && (
                    <Container maxW={CONTAINER_MAX_WIDTH_1} pb={{ base: 6, md: 8, lg: 12 }}>
                      <ViewMoreListings
                        listingId={listing?.id}
                        categoryId={listing?.category.id}
                        categories={categories}
                      />
                    </Container>
                  )}
              </>
            ) : user ? (
              <Container maxW={CONTAINER_MAX_WIDTH_2} mt={10}>
                <ErrorMessageBox>
                  {permissionsErrorMessage}
                  &nbsp;You can add roles to your account from the&nbsp;
                  <Button variant="link" onClick={() => router.push('/account/roles')}>
                    <Text as="span" textDecoration="underline">
                      Marketplace Roles
                    </Text>
                  </Button>
                  &nbsp;page.
                </ErrorMessageBox>
              </Container>
            ) : userLoaded ? (
              <Container maxW={CONTAINER_MAX_WIDTH_1} mt={10}>
                <LoginPrompt message="to view this page." showWithSignup={true} />
              </Container>
            ) : (
              <ListingPageSkeleton />
            )
          ) : (
            <ListingPageSkeleton />
          )}
          {!isBidder && rolesToAdd.length > 0 && hasSelectedRole !== null && (
            <AddUserRoleModal
              isOpen={openRegisterBidModal}
              onClose={() => setOpenRegisterBidModal(false)}
              rolesToAdd={rolesToAdd}
              modalTitle="Activate Bidder Role"
              submitButtonText="Register to Bid"
              hasSelectedRole={hasSelectedRole}
            />
          )}
          {categoryTypeLeadOrDirectory(listing?.category?.type) && (
            <ContactSellerModal
              isOpen={isOpen}
              onClose={onClose}
              listing={listing}
              marketplace_name={marketplace?.name}
            />
          )}
          <CategorySelectorModal
            isOpen={isOpenCategorySelectorModal}
            onClose={onCloseCategorySelectorModal}
            onUpdateCategory={updateCategoryListingHandler}
            selectedCategoryId={listing?.category?.id.toString()}
          />
          <Modal
            isOpen={isSharing}
            onClose={() => setIsSharing(false)}
            closeOnOverlayClick={false}
            closeOnEsc={false}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalBody py={12}>
                <Flex direction="column" justifyItems="center" alignItems="center" gap={6}>
                  <Text fontSize="3xl" fontWeight="semibold">
                    Sharing listing...
                  </Text>
                  <Spinner size="xl" />
                </Flex>
              </ModalBody>
            </ModalContent>
          </Modal>

          <Modal
            isOpen={showSharingModal}
            onClose={() => setShowSharingModal(false)}
            closeOnOverlayClick={false}
            closeOnEsc={false}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Share Listing</ModalHeader>
              <ModalCloseButton />
              <ModalBody py={12}>
                <Flex direction="column" justifyItems="center" alignItems="center" gap={4}>
                  {connectedSocialProfiles.map((profile) => (
                    <Button
                      minW="300px"
                      key={profile.platform}
                      leftIcon={getSocialProfileIcon({
                        networkName: profile.platform,
                        fillColor: theme.colors.mainText,
                      })}
                      onClick={() => shareListing(profile.platform)}
                    >
                      Share to {capitalize(profile.platform)}
                    </Button>
                  ))}
                </Flex>
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      )}
      {listing && <ListingSelfReportedSoldPriceModal listing={listing} setListing={setListing} />}
    </>
  );
};

export const getServerSideProps = (async ({ req, res, params }) => {
  try {
    const slug = (params?.slug as string) ?? '';
    const id = slug.split('-').at(-1) ?? '';
    const hostname = (req.headers?.host || '').split(':')[0];
    const [listingResponse, marketplaceResponse] = await Promise.all([
      fetch(`${process.env.NEXT_PUBLIC_API_HOST}/listings/${id}/`),
      fetch(`${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/${hostname}/hostname/`),
    ]);

    res.setHeader('Cache-Control', 's-maxage=10, stale-while-revalidate=59');

    // Check response status for both API calls before parsing JSON
    if (!listingResponse.ok || !marketplaceResponse.ok) {
      return {
        notFound: true,
      };
    }

    const listingData: Listing = await listingResponse.json();
    const marketplace = await marketplaceResponse.json();

    if (!listingData || !marketplace) {
      return {
        notFound: true,
      };
    }
    if (
      listingData &&
      listingData.status &&
      !PREFETCHED_LISTING_STATUSES.includes(listingData.status)
    ) {
      // this will pass a 404 code to the browser, but the page will still render
      // this should help prevent Google from indexing private/expired listings but remain accessible to humans
      res.statusCode = 404;
    }

    if (
      (listingData?.category?.marketplace_id ?? '').toString() !==
      (marketplace?.id ?? '').toString()
    ) {
      // If the listing's category does not belong to the marketplace, return 404
      return {
        notFound: true,
      };
    }

    return {
      props: { listingData, marketplace },
    };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
}) satisfies GetServerSideProps<{ listingData: Listing; marketplace: MarketplaceInfo }>;

export default ViewListing;
